// once we have decided on a standard string, we can have this logic in a small composable / helper

import { Device } from '@/helpers/composables/useUserAgent';
import { getPrimeAssociatesSuffix } from '@/helpers/promotion-helper';
import { ObjectType } from '@/@types/graphql-types';

export type ExperimentAffiliationPlacement =
	| 'ft'
	| 'ro'
	| 'po'
	| 'sqtv'
	| 'qtv'
	| 'buy'
	| 'rent'
	| 'buyrent'
	| 'cta'
	| 'tvod'
	| 'play'
	| 'amzch';

/*
 * Generates the affiliation tag for experiments in the new standard format.
 * Example tag: exp-dstack-v1-us-ft
 *
 * Params:
 * - affiliationShortId: The short identifier for the experiment affiliation (e.g., 'dstack'). This should match the affiliationShortId in experiment-config.
 * - variant: The variant name (e.g., 'control', 'variant_1', etc.).
 * - country: The country code (e.g., 'US', 'DE', etc.).
 * - placement: The placement type (e.g., 'ft', 'ro', 'sqtv', 'qtv').
 * - device: Identifies 'web' or 'web-mob'
 * - providerShortName: Optional provider short name to determine if a prime suffix is needed (e.g., 'prv', 'amp').
 * - tagProviderName: Optional string appended to the tag to identify the provider (e.g., 'amzch', 'atpch'). Use for sub-providers or channel variants where the provider needs to be distinguishable in the tag.
 * - technicalName: Optional full technical name of the provider (e.g., 'amazonprimevideo'). Used alongside providerShortName to determine if a prime suffix should be applied for amazon channel variants.
 *
 * Returns:
 * - A formatted affiliation tag string.
 */

function getDeviceShort(device?: Device) {
	// TODO - consider how to handle 'unknowm' when working with Prime, as a third device param would muliply the number of tags created
	// I would opt for assuming uknown as 'web'. Can also go for 'nkn' | 'oth'.
	if (!device || device === 'unknown') return null;

	return device === 'mobile' ? 'mob' : 'web';
}

function getPageTypeShortName(pageType: ObjectType) {
	if (pageType === ObjectType.Movie) return 'mov';
	if (pageType === ObjectType.Show) return 'sho';
	if (pageType === ObjectType.ShowSeason) return 'sea';
	return null;
}

export function getExperimentAffiliateTag({
	affiliationShortId,
	variant,
	country,
	placement,
	providerShortName,
	deviceType,
	objectType,
	tagProviderName,
	technicalName,
}: {
	affiliationShortId: string;
	variant: string;
	country: string;
	placement: ExperimentAffiliationPlacement;
	providerShortName?: string | null;
	deviceType?: Device;
	objectType?: ObjectType;
	tagProviderName?: string | null;
	technicalName?: string;
}) {
	// ct, v1, v2, ...
	const variantShort = variant === 'control' ? 'ct' : variant.replace('variant_', 'v');

	const deviceTypeShort = getDeviceShort(deviceType);
	const objectTypeShort = getPageTypeShortName(objectType as ObjectType);

	const baseAffiliateTag = [
		'exp',
		affiliationShortId,
		variantShort,
		country.toLowerCase(),
		placement,
		deviceTypeShort,
		objectTypeShort,
		tagProviderName,
	]
		.filter(Boolean)
		.join('-');

	if (!['prv', 'amp', 'amz'].includes(providerShortName || '') && !technicalName?.includes('amazon'))
		return baseAffiliateTag;

	// example tag with prime suffix: exp-dstack-v1-us-qtv-20
	const primeSuffix = getPrimeAssociatesSuffix(country);

	return primeSuffix ? `${baseAffiliateTag}-${primeSuffix}` : baseAffiliateTag;
}
